// src/components/Landing/PlaceholderLanding.jsx
// Eventory landing page — speaks to the Lagos caterer and event planner, NOT
// to investors. Investor copy ($ figures, market sizing, "platform OS", deck
// language) lives at /pitch. Here, every line answers "what's in it for the
// person running the room on Saturday night."

import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { trackEvent } from '../../utils/analytics';
import { Box, Flex, Text, Heading, Button, HStack, VStack, Container } from '@chakra-ui/react';
import { MarketingFooter } from './MarketingShell';
import { Reveal, RevealGroup, CountUp, useInView, prefersReducedMotion } from './motion';
import {
	IconWifiOff,
	IconPhone,
	IconGuests,
	IconChart,
	IconChef,
	IconCelebrate,
	IconOverview,
} from '../Shared/FigmaIcons';
import {
	captureAttributionFromLocation,
	initializeAnalytics,
	trackPageView,
} from '../../utils/analytics';

// ---- Design tokens ----
const BRAND = '#3D1E6D';
const BRAND_DEEP = '#241043';
const BRAND_LIGHT = '#5B3A8A';
const ACCENT = '#FB8601';
const ACCENT_DEEP = '#B14E00';
const INK = '#111113';
const MUTED = '#6B7280';
const CREAM = '#FBFBF7';
const LINE = 'rgba(17, 17, 19, 0.08)';

const HEAD_FONT = "'Poppins', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

// =============================================================================
// Atoms
// =============================================================================

const Kicker = ({ children, color = ACCENT }) => (
	<Text
		fontFamily={HEAD_FONT}
		fontSize={{ base: '11px', md: '13px' }}
		fontWeight="700"
		letterSpacing="0.32em"
		textTransform="uppercase"
		color={color}
	>
		{children}
	</Text>
);

const Section = ({ children, bg = 'white', ink = INK, id, py, ...rest }) => (
	<Box
		as="section"
		id={id}
		bg={bg}
		color={ink}
		py={py ?? { base: 20, md: 32 }}
		px={{ base: 6, md: 10, lg: 16 }}
		position="relative"
		overflow="hidden"
		{...rest}
	>
		<Container maxW="1400px" px={0} position="relative" zIndex={1}>
			{children}
		</Container>
	</Box>
);

// =============================================================================
// Device-mockup frames — make raw screenshots feel like product photography
// =============================================================================

// Phone frame for the guest-ordering screenshot.
// `hideFrameMaxW` lets a parent wrapper own the sizing (used by the product
// showcase so a numbered badge can anchor to the actual phone, not the column).
const PhoneFrame = ({ src, alt, hideFrameMaxW = false }) => (
	<Box
		position="relative"
		w="100%"
		maxW={hideFrameMaxW ? undefined : { base: '280px', md: '340px' }}
		mx={hideFrameMaxW ? 0 : 'auto'}
		_before={{
			content: '""',
			position: 'absolute',
			inset: '-20% -10% -10% -20%',
			bg: `radial-gradient(ellipse at center, ${ACCENT}33 0%, transparent 65%)`,
			filter: 'blur(40px)',
			zIndex: -1,
		}}
	>
		<Box
			bg="#0e0e10"
			borderRadius="42px"
			p="10px"
			boxShadow="0 30px 70px rgba(17,17,19,0.25), 0 4px 12px rgba(17,17,19,0.18)"
		>
			<Box
				bg="white"
				borderRadius="34px"
				overflow="hidden"
				position="relative"
				aspectRatio="9/19"
			>
				{/* Speaker / camera bar at top */}
				<Box
					position="absolute"
					top="10px"
					left="50%"
					transform="translateX(-50%)"
					w="92px"
					h="24px"
					bg="#0e0e10"
					borderRadius="full"
					zIndex={2}
				/>
				<Box
					as="img"
					src={src}
					alt={alt}
					w="100%"
					h="100%"
					objectFit="cover"
					objectPosition="top center"
				/>
			</Box>
		</Box>
	</Box>
);

// Tablet frame for the kitchen-dashboard screenshot
const TabletFrame = ({ src, alt }) => (
	<Box
		position="relative"
		w="100%"
		_before={{
			content: '""',
			position: 'absolute',
			inset: '-12% -8% -8% -12%',
			bg: `radial-gradient(ellipse at center, ${BRAND_LIGHT}33 0%, transparent 65%)`,
			filter: 'blur(45px)',
			zIndex: -1,
		}}
	>
		<Box
			bg="#0e0e10"
			borderRadius="22px"
			p="12px"
			boxShadow="0 30px 70px rgba(17,17,19,0.25), 0 4px 12px rgba(17,17,19,0.18)"
		>
			<Box
				bg="white"
				borderRadius="14px"
				overflow="hidden"
				aspectRatio="16/10"
			>
				<Box
					as="img"
					src={src}
					alt={alt}
					w="100%"
					h="100%"
					objectFit="cover"
					objectPosition="top left"
				/>
			</Box>
		</Box>
	</Box>
);

// Browser-window frame for the admin-dashboard screenshot
const BrowserFrame = ({ src, alt, label = 'eventory.app' }) => (
	<Box
		position="relative"
		w="100%"
		_before={{
			content: '""',
			position: 'absolute',
			inset: '-10% -6% -6% -10%',
			bg: `radial-gradient(ellipse at center, ${ACCENT}22 0%, transparent 65%)`,
			filter: 'blur(40px)',
			zIndex: -1,
		}}
	>
		<Box
			bg="white"
			borderRadius="14px"
			overflow="hidden"
			border={`1px solid ${LINE}`}
			boxShadow="0 30px 80px rgba(17,17,19,0.12), 0 4px 12px rgba(17,17,19,0.06)"
		>
			{/* Window chrome */}
			<Flex
				bg="#f5f5f7"
				borderBottom={`1px solid ${LINE}`}
				h="38px"
				align="center"
				px={4}
				gap={4}
			>
				<HStack gap={2}>
					<Box w="11px" h="11px" borderRadius="full" bg="#ff5f57" />
					<Box w="11px" h="11px" borderRadius="full" bg="#febc2e" />
					<Box w="11px" h="11px" borderRadius="full" bg="#28c840" />
				</HStack>
				<Box flex={1} display="flex" justifyContent="center">
					<Box
						bg="white"
						border={`1px solid ${LINE}`}
						borderRadius="md"
						px={3}
						py="3px"
						fontSize="11px"
						color={MUTED}
						fontFamily={BODY_FONT}
						maxW="240px"
						w="100%"
						textAlign="center"
					>
						{label}
					</Box>
				</Box>
				<Box w="40px" />
			</Flex>
			<Box bg="white" aspectRatio="16/10">
				<Box
					as="img"
					src={src}
					alt={alt}
					w="100%"
					h="100%"
					objectFit="cover"
					objectPosition="top left"
				/>
			</Box>
		</Box>
	</Box>
);

// =============================================================================
// Sticky top navigation
// =============================================================================

const TopNav = () => {
	const { isAuthenticated } = useContext(AuthContext);
	const [scrolled, setScrolled] = useState(false);
	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 24);
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	const goToSection = (id) => {
		const el = document.getElementById(id);
		if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	return (
		<Box
			position="fixed"
			top={{ base: 3, md: 5 }}
			left={0}
			right={0}
			zIndex={100}
			px={{ base: 3, md: 6 }}
		>
			<Flex
				mx="auto"
				maxW="1280px"
				h={{ base: '56px', md: '72px' }}
				bg={scrolled ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.7)'}
				backdropFilter="blur(16px)"
				borderRadius="full"
				px={{ base: 4, md: 6 }}
				align="center"
				justify="space-between"
				border={`1px solid ${LINE}`}
				transition="all 0.2s ease"
				boxShadow={scrolled ? '0 8px 24px rgba(17,17,19,0.06)' : 'none'}
			>
				<RouterLink to="/" aria-label="Eventory home">
					<Box as="img" src="/images/eventory-logo.svg" alt="Eventory" h={{ base: '22px', md: '28px' }} w="auto" />
				</RouterLink>

				<HStack gap={{ base: 5, md: 8 }} display={{ base: 'none', md: 'flex' }} fontFamily={BODY_FONT}>
					<Box as="button" onClick={() => goToSection('product')} fontSize="15px" fontWeight="600" color={INK} _hover={{ color: ACCENT_DEEP }} bg="transparent" border="none">
						See the product
					</Box>
					<Box as={RouterLink} to="/pricing" fontSize="15px" fontWeight="600" color={INK} _hover={{ color: ACCENT_DEEP, textDecoration: 'none' }} textDecoration="none">
						Pricing
					</Box>
				</HStack>

				<HStack gap={3}>
					{!isAuthenticated && (
						<Box
							as={RouterLink}
							to="/login"
							display={{ base: 'none', sm: 'block' }}
							fontFamily={BODY_FONT}
							fontSize={{ base: '13px', md: '14px' }}
							fontWeight="600"
							color={INK}
							textDecoration="none"
							_hover={{ color: ACCENT_DEEP }}
						>
							Sign in
						</Box>
					)}
					<Button
						as={RouterLink}
						to={isAuthenticated ? '/dashboard' : '/register'}
						bg={INK}
						color="white"
						borderRadius="full"
						px={{ base: 4, md: 6 }}
						h={{ base: '36px', md: '44px' }}
						fontSize={{ base: '13px', md: '14px' }}
						fontWeight="700"
						letterSpacing="0.02em"
						textDecoration="none"
						_hover={{ bg: BRAND, textDecoration: 'none' }}
					>
						{isAuthenticated ? 'My events →' : 'Get started'}
					</Button>
				</HStack>
			</Flex>
		</Box>
	);
};

// Auth-aware primary CTA: logged-in visitors go to their events, not signup.
const GetStartedCta = ({ label, ...props }) => {
	const { isAuthenticated } = useContext(AuthContext);
	return (
		<Button
			as={RouterLink}
			to={isAuthenticated ? '/dashboard' : '/register'}
			bg={ACCENT}
			color="white"
			borderRadius="full"
			fontWeight="700"
			textDecoration="none"
			_hover={{ bg: ACCENT_DEEP, textDecoration: 'none' }}
			{...props}
		>
			{isAuthenticated ? 'Open my events →' : label || 'Get started free'}
		</Button>
	);
};

// =============================================================================
// WHATSAPP CLICK-TO-CHAT — the corner chat bubble
// =============================================================================
// Founder direction 2026-07-18: WhatsApp lives ONLY as the sticky bottom-right
// button — in-flow CTAs are the register flow ("Try it now") so the headline
// ("Stop running events on WhatsApp") and the CTA no longer contradict each
// other. Research context: docs/strategy/whatsapp-cta-research.md. The number
// ships via build env — when unset, the bubble simply doesn't render.
const WHATSAPP_NUMBER = String(process.env.REACT_APP_WHATSAPP_NUMBER || '').replace(/[^0-9]/g, '');
const WA_STICKY_TEXT = 'Hi! I have a question about Eventory.';
const waHref = () => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WA_STICKY_TEXT)}`;

const WhatsAppIcon = ({ size = 20 }) => (
	<Box as="svg" viewBox="0 0 24 24" w={`${size}px`} h={`${size}px`} fill="currentColor" aria-hidden="true">
		<path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.3-1.39a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.44 9.9-9.9 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.23 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29Z" />
	</Box>
);

// Sticky chat button — the highest-converting placement in click-to-chat
// studies. All viewports: it's now the ONLY WhatsApp entry on the page.
const StickyWhatsApp = () => {
	if (!WHATSAPP_NUMBER) return null;
	return (
		<Box
			as="a"
			href={waHref()}
			target="_blank"
			rel="noopener noreferrer"
			onClick={() => trackEvent('whatsapp_cta_click', { placement: 'sticky' })}
			position="fixed"
			bottom="20px"
			right="16px"
			zIndex={90}
			display="flex"
			alignItems="center"
			justifyContent="center"
			w="56px"
			h="56px"
			borderRadius="full"
			bg="#25D366"
			color="white"
			boxShadow="0 10px 24px rgba(11,59,39,0.35)"
			aria-label="Chat with Eventory on WhatsApp"
		>
			<WhatsAppIcon size={28} />
		</Box>
	);
};

// =============================================================================
// HERO — operator-first
// =============================================================================

const Hero = () => (
	<Box
		as="section"
		position="relative"
		bg={BRAND_DEEP}
		color="white"
		pt={{ base: 32, md: 44 }}
		pb={{ base: 24, md: 32 }}
		px={{ base: 6, md: 10, lg: 16 }}
		overflow="hidden"
	>
		<Box
			position="absolute"
			top="-20%"
			left="-15%"
			w="60%"
			h="80%"
			bg={`radial-gradient(ellipse at center, ${ACCENT}55 0%, ${ACCENT}10 35%, transparent 70%)`}
			filter="blur(60px)"
			zIndex={0}
			className="ev-glow-a"
		/>
		<Box
			position="absolute"
			bottom="-25%"
			right="-20%"
			w="70%"
			h="90%"
			bg={`radial-gradient(ellipse at center, ${BRAND_LIGHT}77 0%, ${BRAND_LIGHT}22 35%, transparent 70%)`}
			filter="blur(80px)"
			zIndex={0}
			className="ev-glow-b"
		/>
		<Box
			position="absolute"
			inset="0"
			opacity={0.06}
			backgroundImage="linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)"
			backgroundSize="80px 80px"
			zIndex={0}
		/>

		<Container maxW="1400px" px={0} position="relative" zIndex={1}>
			<Box display="grid" gridTemplateColumns={{ base: '1fr', lg: '7fr 5fr' }} gap={{ base: 12, lg: 20 }} alignItems="center">
				<VStack align="flex-start" spacing={0} className="ev-hero-stagger">
					<HStack gap={3} flexWrap="wrap">
						<Kicker color={ACCENT}>For event planners in Lagos</Kicker>
						<Box
							as="span"
							px={3}
							py={1}
							borderRadius="full"
							border="1px solid rgba(255,255,255,0.28)"
							bg="rgba(255,255,255,0.08)"
							fontSize="12px"
							fontWeight="700"
							letterSpacing="0.08em"
							textTransform="uppercase"
							color="rgba(255,255,255,0.85)"
							fontFamily={HEAD_FONT}
						>
							Pilot programme · first events free
						</Box>
					</HStack>

					<Heading
						as="h1"
						className="ev-hero-lcp"
						mt={{ base: 5, md: 6 }}
						fontFamily={HEAD_FONT}
						fontWeight="700"
						fontSize={{ base: '44px', md: '80px', lg: '108px' }}
						lineHeight="0.95"
						letterSpacing="-0.035em"
						color="white"
					>
						Stop running events{' '}
						<Box as="span" color={ACCENT}>on WhatsApp.</Box>
					</Heading>

					<Text
						mt={{ base: 6, md: 8 }}
						fontSize={{ base: 'lg', md: '2xl' }}
						color="rgba(255,255,255,0.78)"
						maxW="640px"
						lineHeight="1.45"
					>
						Guests order from their phones and your kitchen sees every order as it happens. You direct your team with real-time feedback and end the night knowing exactly what was served.
					</Text>

					<HStack mt={{ base: 8, md: 12 }} gap={4} flexWrap="wrap">
						<GetStartedCta label="Try it now" px={8} h={{ base: '52px', md: '60px' }} fontSize={{ base: 'md', md: 'lg' }} />
						<Button
							as={RouterLink}
							to="/demo"
							variant="outline"
							borderColor="rgba(255,255,255,0.35)"
							color="white"
							borderRadius="full"
							px={8}
							h={{ base: '52px', md: '60px' }}
							fontSize={{ base: 'md', md: 'lg' }}
							fontWeight="700"
							bg="transparent"
							textDecoration="none"
							_hover={{ bg: 'rgba(255,255,255,0.06)', borderColor: 'white', textDecoration: 'none' }}
						>
							See a demo
						</Button>
					</HStack>

					<HStack mt={{ base: 6, md: 8 }} gap={6} fontSize="sm" color="rgba(255,255,255,0.6)" flexWrap="wrap">
						<HStack gap={2}>
							<Box w="6px" h="6px" borderRadius="full" bg={ACCENT} />
							<Text>No app for guests to download</Text>
						</HStack>
					</HStack>
				</VStack>

				{/* Hero composite — phone front-and-centre, surrounded by floating
				   "live event" moments that tell the story of a wedding service in
				   motion. The product screenshots stay supporting cast; the
				   narrative tokens do the emotional lift. */}
				<Box position="relative" w="100%" h={{ base: '420px', md: '600px' }} display={{ base: 'none', md: 'block' }}>
					{/* Back card — admin (browser frame), pushed further behind */}
					<Box
						position="absolute"
						top="6%"
						right="-4%"
						w="72%"
						className="ev-hero-device" style={{ "--ev-tilt": "3deg" }}
						opacity={0.7}
						filter="saturate(0.85)"
					>
						<BrowserFrame src="/images/admin_overview.webp" alt="Admin overview" label="eventory.app / event" />
					</Box>
					{/* Mid card — kitchen (tablet) */}
					<Box
						position="absolute"
						bottom="0%"
						left="-4%"
						w="52%"
						className="ev-hero-device ev-hero-device-2" style={{ "--ev-tilt": "-5deg" }}
						opacity={0.85}
					>
						<TabletFrame src="/images/kitchen_dashboard.webp" alt="Kitchen dashboard" />
					</Box>
					{/* Front card — phone (the hero) */}
					<Box
						position="absolute"
						bottom="6%"
						right="14%"
						w="34%"
						className="ev-hero-device ev-hero-device-3" style={{ "--ev-tilt": "4deg" }}
						zIndex={2}
					>
						<PhoneFrame src="/images/guest_menu.webp" alt="Guest ordering on a phone" />
					</Box>

					{/* Floating event-moment tokens — these tell the live story */}
					<Box
						position="absolute"
						top="14%"
						left="6%"
						bg="white"
						color={BRAND_DEEP}
						borderRadius="14px"
						px={4}
						py={3}
						boxShadow="0 18px 36px rgba(0,0,0,0.22)"
						zIndex={3}
						maxW="200px"
						className="ev-float" style={{ "--ev-tilt": "-3deg" }}
					>
						<Text fontSize="xs" color={ACCENT_DEEP} fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">New order</Text>
						<Text fontSize="sm" fontWeight="600" mt={1}>Table 12 · Adunni</Text>
						<Text fontSize="xs" color="rgba(0,0,0,0.55)" mt={0.5}>Jollof, suya, zobo</Text>
					</Box>

					<Box
						position="absolute"
						top="48%"
						right="2%"
						bg={ACCENT}
						color="white"
						borderRadius="14px"
						px={4}
						py={3}
						boxShadow="0 18px 36px rgba(251,134,1,0.4)"
						zIndex={3}
						maxW="180px"
						className="ev-float ev-float-2" style={{ "--ev-tilt": "4deg" }}
					>
						<Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" color="rgba(255,255,255,0.85)">Ready to serve</Text>
						<Text fontSize="sm" fontWeight="700" mt={1}>Table 4 · 6 plates</Text>
					</Box>

					<Box
						position="absolute"
						bottom="22%"
						left="14%"
						bg="white"
						color={BRAND_DEEP}
						borderRadius="14px"
						px={4}
						py={3}
						boxShadow="0 18px 36px rgba(0,0,0,0.22)"
						zIndex={3}
						maxW="220px"
						className="ev-float ev-float-3" style={{ "--ev-tilt": "2deg" }}
					>
						<HStack gap={2} mb={1}>
							<Box w="8px" h="8px" borderRadius="full" bg="#16A34A" />
							<Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" color="rgba(0,0,0,0.6)">Live</Text>
						</HStack>
						<Text fontSize="sm" fontWeight="600">182 of 240 served</Text>
					</Box>
				</Box>
			</Box>
		</Container>
	</Box>
);

// =============================================================================
// TRUST STRIP — one line, not a stat wall (founder feedback 2026-07-18)
// =============================================================================

const TrustStrip = () => (
	<Box bg={CREAM} py={{ base: 8, md: 10 }} px={{ base: 6, md: 10, lg: 16 }} borderBottom={`1px solid ${LINE}`}>
		<Container maxW="1400px" px={0}>
			<Text
				fontFamily={HEAD_FONT}
				fontWeight="600"
				fontSize={{ base: '18px', md: '24px' }}
				color={BRAND}
				textAlign="center"
				lineHeight="1.4"
			>
				Whether 10 or 1,000 guests, set up your event catering in just{' '}
				<Box as="span" color={ACCENT_DEEP}>10 minutes.</Box>
			</Text>
		</Container>
	</Box>
);

// =============================================================================
// PRODUCT — what the guest, kitchen, and you each see
// =============================================================================

// =============================================================================
// LIVE DEMO — the product actually running, not a screenshot of it
// =============================================================================
// This is the page's centrepiece active element: a real screen recording of a
// guest ordering while the kitchen screen receives the ticket, captured against
// a live instance (scripts/record_demo.js). It autoplays muted and loops, so
// the page moves on its own the moment a visitor reaches it.

const LiveDemo = () => {
	const [ref, inView] = useInView({ threshold: 0.25 });
	const videoRef = useRef(null);

	// Respect Save-Data and reduced-motion: show the poster and let the visitor
	// press play. Save-Data opt-in is high across Africa and this file is ~1 MB.
	const conn = typeof navigator !== 'undefined' ? navigator.connection : null;
	const saveData = !!(conn && conn.saveData);
	const slowLink = !!(conn && /^(slow-)?2g$|^3g$/.test(conn.effectiveType || ''));
	const autoplayAllowed = !saveData && !slowLink && !prefersReducedMotion();

	// Only play while on screen, and never start it if the visitor opted out.
	useEffect(() => {
		const v = videoRef.current;
		if (!v || !autoplayAllowed) return;
		if (inView) {
			const p = v.play();
			if (p && p.catch) p.catch(() => {}); // autoplay policy, ignore
		} else {
			v.pause();
		}
	}, [inView, autoplayAllowed]);

	return (
		<Box
			as="section"
			ref={ref}
			bg={BRAND_DEEP}
			color="white"
			py={{ base: 16, md: 24 }}
			px={{ base: 6, md: 10, lg: 16 }}
			position="relative"
			overflow="hidden"
		>
			<Box
				position="absolute"
				top="-30%"
				right="-10%"
				w="60%"
				h="100%"
				bg={`radial-gradient(ellipse at center, ${ACCENT}33 0%, transparent 65%)`}
				filter="blur(80px)"
				zIndex={0}
			/>
			<Container maxW="1400px" px={0} position="relative" zIndex={1}>
				<Reveal>
					<Box maxW="820px" mb={{ base: 8, md: 12 }}>
						<Kicker color={ACCENT}>Watch it run</Kicker>
						<Heading
							as="h2"
							mt={4}
							fontFamily={HEAD_FONT}
							fontWeight="700"
							fontSize={{ base: '32px', md: '52px' }}
							lineHeight="1.05"
							letterSpacing="-0.025em"
							color="white"
						>
							A guest orders. The kitchen already knows.
						</Heading>
						<Text mt={5} fontSize={{ base: 'md', md: 'lg' }} color="rgba(255,255,255,0.72)" lineHeight="1.6">
							A guest orders on the left. On the right, the kitchen picks up the ticket,
							the server delivers it, and the planner watches the numbers move. One take,
							no edits.
						</Text>
					</Box>
				</Reveal>

				<Reveal delay={0.1}>
					<Box
						borderRadius={{ base: '16px', md: '24px' }}
						overflow="hidden"
						border="1px solid rgba(255,255,255,0.14)"
						boxShadow="0 40px 90px rgba(0,0,0,0.45)"
						bg="#241043"
						lineHeight="0"
					>
						<Box
							as="video"
							ref={videoRef}
							src="/eventory-demo.mp4"
							poster="/eventory-demo-poster.webp"
							muted
							loop
							playsInline
							preload={autoplayAllowed ? 'metadata' : 'none'}
							controls
							w="100%"
							h="auto"
							display="block"
							aria-label="Eventory demo: a guest orders from their phone while the kitchen screen receives the ticket"
						/>
					</Box>
				</Reveal>
			</Container>
		</Box>
	);
};

// =============================================================================
// PROOF CAROUSEL — rotating evidence, all of it real
// =============================================================================

// The pilot numbers live in PilotStory directly below, so this section carries
// the human evidence instead of repeating the same three stats twice.
const PROOF_POINTS = [
	{
		kicker: 'What the data showed',
		stat: '20',
		statLabel: 'years without this data',
		body: 'The caterer had never seen item-by-item demand for a service before. Jollof won. It usually does.',
	},
	{
		kicker: 'Catering manager · Lagos',
		quote: 'I don’t actually know what each table ate. So I keep underselling on the next quote.',
	},
	{
		kicker: 'Wedding planner · Lagos',
		quote: 'The back-and-forth during dinner service is what kills me. I’m on WhatsApp the entire night.',
	},
];


const ProofWall = () => (
	<Section bg={CREAM} py={{ base: 16, md: 24 }}>
		<Reveal>
			<Box maxW="900px">
				<Kicker>The proof</Kicker>
				<Heading
					as="h2"
					mt={4}
					fontFamily={HEAD_FONT}
					fontWeight="700"
					fontSize={{ base: '32px', md: '48px' }}
					lineHeight="1.05"
					letterSpacing="-0.025em"
					color={INK}
				>
					What one real event produced.
				</Heading>
			</Box>
		</Reveal>

		{/* A static wall, deliberately. The rotating carousel this replaced showed
			one point at a time, so most of the evidence sat hidden behind a
			timer, and its pause-on-hover did not exist on touch at all
			(WCAG 2.2.2 Level A). Everything is on screen at once now. */}
		<Box
			mt={{ base: 8, md: 12 }}
			display="grid"
			gridTemplateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
			gap={{ base: 4, md: 5 }}
			alignItems="stretch"
		>
			{PROOF_POINTS.map((s, i) => (
				<Reveal key={s.kicker + i} delay={i * 0.07} y={20} style={{ height: '100%' }}>
					<Box
						h="100%"
						bg="white"
						border={`1px solid ${LINE}`}
						borderRadius="20px"
						p={{ base: 6, md: 7 }}
						display="flex"
						flexDirection="column"
						transition="transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease"
						_hover={{ transform: 'translateY(-4px)', boxShadow: '0 18px 40px rgba(61,30,109,0.10)' }}
					>
						<Kicker>{s.kicker}</Kicker>
						{s.quote ? (
							<Text
								mt={4}
								fontFamily={HEAD_FONT}
								fontStyle="italic"
								fontWeight="500"
								fontSize={{ base: 'lg', md: 'xl' }}
								lineHeight="1.4"
								color={BRAND}
							>
								“{s.quote}”
							</Text>
						) : (
							<>
								<Flex mt={4} align="baseline" gap={2}>
									<Text
										fontFamily={HEAD_FONT}
										fontWeight="700"
										fontSize={{ base: '48px', md: '60px' }}
										lineHeight="1"
										color={ACCENT}
									>
										<CountUp
											to={parseFloat(s.stat)}
											decimals={s.stat.includes('.') ? 1 : 0}
											suffix={s.statSuffix || ''}
										/>
									</Text>
									<Text fontSize={{ base: 'sm', md: 'md' }} color={MUTED} fontWeight="600">
										{s.statLabel}
									</Text>
								</Flex>
								<Text mt={3} fontSize={{ base: 'sm', md: 'md' }} color={MUTED} lineHeight="1.6">
									{s.body}
								</Text>
							</>
						)}
					</Box>
				</Reveal>
			))}
		</Box>
	</Section>
);


const ProductShowcase = () => {
	const features = [
		{
			kicker: 'What your guest sees',
			title: 'Order from the table',
			body: 'Guests scan the QR on their table and the menu opens in their browser. Nothing to download, and when a dish runs out it comes off the menu.',
			frame: 'phone',
			img: '/images/guest_menu.webp',
			alt: 'Guest ordering on their phone',
			align: 'right',
		},
		{
			kicker: 'What your kitchen sees',
			title: 'Tickets land in the kitchen instantly',
			body: 'Each station has its own live queue, so the grill team only sees grill orders. Marking a dish ready takes one tap.',
			frame: 'tablet',
			img: '/images/kitchen_dashboard.webp',
			alt: 'Kitchen team seeing live orders on a tablet',
			align: 'left',
		},
		{
			kicker: 'What you see',
			title: 'Your whole event on one screen',
			body: 'Set up tables, stations and menus, then follow the whole night from one dashboard.',
			frame: 'browser',
			img: '/images/admin_overview.webp',
			alt: 'The planner control room',
			align: 'right',
		},
	];

	return (
		<Section id="product" bg="white" py={{ base: 24, md: 36 }}>
			<Box maxW="900px">
				<Heading
					as="h2"
					fontFamily={HEAD_FONT}
					fontWeight="700"
					fontSize={{ base: '36px', md: '60px', lg: '72px' }}
					lineHeight="1.04"
					letterSpacing="-0.025em"
					color={INK}
				>
					Everyone sees the{' '}
					<Box as="span" color={ACCENT}>same event, live.</Box>
				</Heading>
			</Box>

			{/* Each view lives in its own panel — founder feedback 2026-07-18:
				the pieces need visible separation, not one continuous scroll. */}
			<VStack mt={{ base: 10, md: 16 }} spacing={{ base: 6, md: 10 }} align="stretch">
				{features.map((f, i) => (
					<Reveal key={f.title} delay={0.05}>
					<Box
						className="ev-panel-lift"
						bg={CREAM}
						border={`1px solid ${LINE}`}
						borderRadius={{ base: '20px', md: '28px' }}
						p={{ base: 6, md: 12 }}
						display="grid"
						gridTemplateColumns={{ base: '1fr', lg: f.align === 'right' ? '5fr 7fr' : '7fr 5fr' }}
						gap={{ base: 10, lg: 20 }}
						alignItems="center"
					>
						<Box order={{ base: 1, lg: f.align === 'right' ? 1 : 2 }}>
							<Kicker color={ACCENT}>{f.kicker}</Kicker>
							<Heading
								as="h3"
								mt={4}
								fontFamily={HEAD_FONT}
								fontWeight="700"
								fontSize={{ base: '30px', md: '44px', lg: '52px' }}
								lineHeight="1.05"
								letterSpacing="-0.02em"
								color={INK}
							>
								{f.title}
							</Heading>
							<Text mt={5} fontSize={{ base: 'md', md: 'lg' }} color={MUTED} lineHeight="1.6">
								{f.body}
							</Text>
						</Box>

						<Box order={{ base: 2, lg: f.align === 'right' ? 2 : 1 }} px={{ base: 4, md: 0 }}>
							{/* Inner wrapper sizes itself to the device frame so the
								numbered badge anchors to the frame, not the wide column.
								Previously the badge floated off to the left of the
								(narrow, centred) phone — feedback from QA. */}
							<Box
								position="relative"
								display="inline-block"
								w="100%"
								maxW={f.frame === 'phone' ? { base: '280px', md: '340px' } : '100%'}
								mx={f.frame === 'phone' ? 'auto' : 0}
							>
								{f.frame === 'phone' && <PhoneFrame src={f.img} alt={f.alt} hideFrameMaxW />}
								{f.frame === 'tablet' && <TabletFrame src={f.img} alt={f.alt} />}
								{f.frame === 'browser' && <BrowserFrame src={f.img} alt={f.alt} label="eventory.app / event" />}
								<Box
									position="absolute"
									top={{ base: '-12px', md: '-20px' }}
									left={{ base: '-12px', md: '-20px' }}
									bg={BRAND}
									color="white"
									borderRadius="full"
									px={4}
									py={2}
									fontSize="11px"
									fontWeight="700"
									letterSpacing="0.12em"
									textTransform="uppercase"
									fontFamily={HEAD_FONT}
									zIndex={2}
									boxShadow="0 6px 16px rgba(61,30,109,0.25)"
								>
									0{i + 1}
								</Box>
							</Box>
						</Box>
					</Box>
					</Reveal>
				))}
			</VStack>
		</Section>
	);
};

// =============================================================================
// MADE FOR THE WAY YOU WORK — was "Built for Lagos / Not Silicon Valley"
// (rewritten to talk about the caterer's real problems, not investor framing)
// =============================================================================

const MadeForLagos = () => (
	<Box
		as="section"
		bg={BRAND_DEEP}
		color="white"
		py={{ base: 24, md: 36 }}
		px={{ base: 6, md: 10, lg: 16 }}
		position="relative"
		overflow="hidden"
	>
		<Box
			position="absolute"
			top="-10%"
			right="-15%"
			w="55%"
			h="80%"
			bg={`radial-gradient(ellipse at center, ${ACCENT}44 0%, transparent 65%)`}
			filter="blur(70px)"
			zIndex={0}
		/>
		<Container maxW="1400px" px={0} position="relative" zIndex={1}>
			<Reveal>
			<Box maxW="900px">
				<Kicker color={ACCENT}>Made for the way you work</Kicker>
				<Heading
					as="h2"
					mt={5}
					fontFamily={HEAD_FONT}
					fontWeight="700"
					fontSize={{ base: '36px', md: '52px', lg: '60px' }}
					lineHeight="1.04"
					letterSpacing="-0.025em"
					color="white"
				>
					The WiFi will drop.{' '}
					<Box as="span" color={ACCENT}>The food still goes out.</Box>
				</Heading>
			</Box>
			</Reveal>

			<Box
				mt={{ base: 10, md: 14 }}
				display="grid"
				gridTemplateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
				gap={{ base: 4, md: 5 }}
			>
				<RevealGroup step={0.07} y={20}>
				{[
					{ Icon: IconWifiOff, t: 'Survives WiFi drops', d: 'Orders queue and sync when the room comes back.' },
					{ Icon: IconPhone, t: 'Guests use their own phones', d: 'No tablets to hand out or chase down.' },
					{ Icon: IconGuests, t: 'Ops Manager on bundles', d: 'We run the floor with you on Standard & Premium.' },
					{ Icon: IconChart, t: 'Client-ready numbers', d: 'Export what was served and send it to the client.' },
				].map(({ Icon, t, d }) => (
					<Box
						key={t}
						bg="rgba(255,255,255,0.04)"
						border="1px solid rgba(255,255,255,0.12)"
						borderRadius="xl"
						p={{ base: 6, md: 7 }}
					>
						<Flex
							w="52px"
							h="52px"
							borderRadius="14px"
							bg={`${ACCENT}22`}
							border={`1px solid ${ACCENT}55`}
							align="center"
							justify="center"
							color={ACCENT}
							mb={4}
						>
							<Icon size={28} />
						</Flex>
						<Heading fontFamily={HEAD_FONT} fontSize={{ base: 'lg', md: 'xl' }} fontWeight="700" color="white" mb={2}>
							{t}
						</Heading>
						<Text fontSize={{ base: 'sm', md: 'md' }} color="rgba(255,255,255,0.72)" lineHeight="1.5">
							{d}
						</Text>
					</Box>
				))}
				</RevealGroup>
			</Box>
		</Container>
	</Box>
);

// =============================================================================
// FOR EVERYONE — the three people in the room
// =============================================================================

const ForEveryone = () => (
	<Section bg="white" py={{ base: 14, md: 20 }}>
		<Reveal>
		<Box maxW="800px" mx="auto">
			<Kicker>Everyone in the room wins</Kicker>
			<Box mt={{ base: 6, md: 8 }} border={`1px solid ${LINE}`} borderRadius="2xl" overflow="hidden">
				{[
					{ Icon: IconChef, hue: ACCENT, who: 'The caterer', rest: 'finally sees which dishes sell and how many plates go out.' },
					{ Icon: IconCelebrate, hue: BRAND, who: 'Guests', rest: 'skip the buffet queue and can see what is still available.' },
					{ Icon: IconOverview, hue: BRAND_LIGHT, who: 'The planner', rest: 'spends the night hosting, not chasing the kitchen.' },
				].map(({ Icon, hue, who, rest }, i) => (
					<Flex
						key={who}
						align="center"
						gap={4}
						px={{ base: 5, md: 7 }}
						py={{ base: 4, md: 5 }}
						borderTop={i ? `1px solid ${LINE}` : 'none'}
					>
						<Flex w="40px" h="40px" minW="40px" borderRadius="12px" bg={`${hue}18`} color={hue} align="center" justify="center">
							<Icon size={22} />
						</Flex>
						<Text fontSize={{ base: 'md', md: 'lg' }} color={INK} lineHeight="1.5">
							<Box as="span" fontWeight="700">{who}</Box> {rest}
						</Text>
					</Flex>
				))}
			</Box>
		</Box>
		</Reveal>
	</Section>
);

// =============================================================================
// TESTIMONIALS
// =============================================================================

// =============================================================================
// PILOT STORY — proof built in public. Real numbers from the pilot event's
// VIP-table service (June 18, 2026), shown, not narrated. This section grows
// with every event we run; nothing here is projected or padded.
// =============================================================================

const PILOT_DISHES = [
	{ name: 'Jollof Rice', plates: 11 },
	{ name: 'Oriental Fried Rice', plates: 9 },
	{ name: 'Seafood Chinese Rice', plates: 8 },
	{ name: 'Ofada Rice', plates: 5 },
	{ name: 'Fried Rice', plates: 5 },
];

const PilotStory = () => {
	const max = Math.max(...PILOT_DISHES.map((d) => d.plates));
	const [chartRef, chartInView] = useInView({ threshold: 0.25 });
	// value/decimals/suffix are split out so CountUp can animate the number and
	// still render the exact final string in the markup.
	const stats = [
		{ v: 18, d: 0, suffix: '', label: 'orders straight to the kitchen' },
		{ v: 43, d: 0, suffix: '', label: 'plates served' },
		{ v: 3.8, d: 1, suffix: 'h', label: 'of live service' },
		{ v: 11, d: 0, suffix: '', label: 'menu items on the night' },
	];
	return (
		<Section bg={BRAND_DEEP} ink="white" py={{ base: 24, md: 32 }}>
			<Box maxW="1100px" mx="auto">
				<Kicker>Our pilot event</Kicker>
				<Heading
					as="h2"
					mt={5}
					fontFamily={HEAD_FONT}
					fontWeight="700"
					fontSize={{ base: '36px', md: '56px' }}
					lineHeight="1.04"
					letterSpacing="-0.025em"
					color="white"
					maxW="760px"
				>
					We ran VIP service at a{' '}
					<Box as="span" color={ACCENT}>90th birthday.</Box>
				</Heading>
				<Text mt={5} fontSize={{ base: 'md', md: 'lg' }} color="rgba(255,255,255,0.7)" maxW="640px" lineHeight="1.6">
					June 2026, Hi-Impact Planet Event Center, Ogun State.
				</Text>

				<Box
					mt={{ base: 10, md: 14 }}
					display="grid"
					gridTemplateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }}
					gap={{ base: 3, md: 5 }}
				>
					{stats.map((s, i) => (
						<Reveal key={s.label} delay={i * 0.07} y={18}>
						<Box bg="rgba(255,255,255,0.06)" border="1px solid rgba(255,255,255,0.12)" borderRadius="16px" p={{ base: 4, md: 6 }} h="100%">
							<Text fontFamily={HEAD_FONT} fontWeight="700" fontSize={{ base: '30px', md: '40px' }} color={ACCENT} lineHeight="1">
								<CountUp to={s.v} decimals={s.d} suffix={s.suffix} />
							</Text>
							<Text mt={2} fontSize={{ base: 'xs', md: 'sm' }} color="rgba(255,255,255,0.7)" lineHeight="1.4">
								{s.label}
							</Text>
						</Box>
						</Reveal>
					))}
				</Box>

				<Box ref={chartRef} mt={{ base: 10, md: 14 }} bg="rgba(255,255,255,0.05)" border="1px solid rgba(255,255,255,0.12)" borderRadius="20px" p={{ base: 5, md: 8 }}>
					<Text fontFamily={HEAD_FONT} fontWeight="700" fontSize={{ base: 'sm', md: 'md' }} color="white" mb={1}>
						What the VIP tables actually ate
					</Text>
					<Text fontSize="xs" color="rgba(255,255,255,0.55)" mb={5}>
						Every plate counted live during service. The caterer had never had this data before.
					</Text>
					<VStack align="stretch" spacing={3}>
						{PILOT_DISHES.map((d, i) => (
							<Box key={d.name} display="grid" gridTemplateColumns={{ base: '120px 1fr 30px', md: '180px 1fr 36px' }} gap={3} alignItems="center">
								<Text fontSize={{ base: 'xs', md: 'sm' }} color="rgba(255,255,255,0.85)" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
									{d.name}
								</Text>
								<Box bg="rgba(255,255,255,0.1)" borderRadius="full" h={{ base: '10px', md: '14px' }} overflow="hidden">
									<Box
										bg={ACCENT}
										h="100%"
										borderRadius="full"
										w={`${(d.plates / max) * 100}%`}
										transformOrigin="left center"
										transform={chartInView ? 'scaleX(1)' : 'scaleX(0)'}
										transition={`transform 0.85s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.08}s`}
										willChange={chartInView ? 'auto' : 'transform'}
									/>
								</Box>
								<Text fontFamily={HEAD_FONT} fontWeight="700" fontSize={{ base: 'sm', md: 'md' }} color="white" textAlign="right">
									<CountUp to={d.plates} />
								</Text>
							</Box>
						))}
					</VStack>
					<Text mt={5} fontSize="xs" color="rgba(255,255,255,0.5)">
						Jollof won. It's always Jollof. 🍚
					</Text>
				</Box>
			</Box>
		</Section>
	);
};

const Testimonials = () => {
	// Pre-launch voice-of-customer from in-depth interviews with Lagos
	// planners and caterers (Jan-Apr 2026). These are paraphrased summaries
	// of recurring frustrations — not signed quotes from named operators.
	// Once paid event #1 closes and an operator agrees to be quoted on the
	// record, swap in their real name + the unedited line.
	const items = [
		{
			initials: '01',
			name: 'Wedding planner interview',
			role: 'Lagos · Jan 2026',
			quote: 'The back-and-forth during dinner service is what kills me. I\'m on WhatsApp the entire night.',
		},
		{
			initials: '02',
			name: 'Corporate event planner interview',
			role: 'Lagos · Feb 2026',
			quote: 'I want everything in one place. I\'m using 4 tools and a Google Sheet.',
		},
		{
			initials: '03',
			name: 'Catering manager interview',
			role: 'Lagos · Apr 2026',
			quote: 'I don\'t actually know what each table ate. So I keep underselling on the next quote.',
		},
	];

	return (
		<Section bg={CREAM} py={{ base: 24, md: 32 }}>
			<Box maxW="900px">
				<Kicker>What planners told us</Kicker>
				<Heading
					as="h2"
					mt={5}
					fontFamily={HEAD_FONT}
					fontWeight="700"
					fontSize={{ base: '36px', md: '56px', lg: '64px' }}
					lineHeight="1.04"
					letterSpacing="-0.025em"
					color={INK}
				>
					From people who've{' '}
					<Box as="span" color={ACCENT}>seen the chaos.</Box>
				</Heading>
				<Text mt={6} fontSize={{ base: 'md', md: 'lg' }} color={MUTED} maxW="640px" lineHeight="1.6">
					From our pre-launch interviews with Lagos planners and caterers, paraphrased to protect identities. We will add named quotes when customers agree to go on the record.
				</Text>
			</Box>

			<Box mt={{ base: 12, md: 16 }} display="grid" gridTemplateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
				{items.map((t) => (
					<Box
						key={t.name}
						bg="white"
						borderRadius="2xl"
						p={{ base: 8, md: 9 }}
						border={`1px solid ${LINE}`}
						position="relative"
						h="100%"
					>
						<Text
							position="absolute"
							top="12px"
							right="20px"
							fontFamily={HEAD_FONT}
							fontWeight="700"
							fontSize="80px"
							color={ACCENT}
							opacity={0.12}
							lineHeight="1"
						>
							“
						</Text>
						<Text fontSize={{ base: 'md', md: 'lg' }} color={INK} lineHeight="1.55" fontStyle="italic">
							{t.quote}
						</Text>
						<HStack mt={6} gap={3} align="center">
							<Flex
								w="40px"
								h="40px"
								bg={`${BRAND}11`}
								borderRadius="full"
								align="center"
								justify="center"
								color={BRAND}
								fontFamily={HEAD_FONT}
								fontWeight="700"
								fontSize="14px"
							>
								{t.initials}
							</Flex>
							<Box>
								<Text fontFamily={HEAD_FONT} fontWeight="700" fontSize="md" color={INK}>
									{t.name}
								</Text>
								<Text fontSize="xs" color={MUTED} letterSpacing="0.04em" textTransform="uppercase" fontWeight="600">
									{t.role}
								</Text>
							</Box>
						</HStack>
					</Box>
				))}
			</Box>
		</Section>
	);
};

// =============================================================================
// FAQ
// =============================================================================

const FAQ = () => {
	const [open, setOpen] = useState(0);
	const items = [
		{
			q: 'How much does it cost?',
			a: 'Your first 2 events are free. After that it\'s ₦15K per event for software-only, ₦95K or ₦135K for the full-service bundles with tablets and an Ops Manager, or ₦75K a month for unlimited events on the Oga plan. Full details are on the pricing page.',
		},
		{
			q: 'Do my guests need to download an app?',
			a: 'No. They scan the QR code on the table and the menu opens in their phone\'s browser.',
		},
		{
			q: 'What happens if the WiFi drops in the middle of dinner?',
			a: 'Your kitchen tablets pre-load before service and keep working through blips. Phones already on the menu queue orders locally and sync when WiFi returns. Only the brand-new arrivals are blocked until the room is back online.',
		},
		{
			q: 'How long does setup take?',
			a: 'On full-service bundles, our Ops Manager sets you up the day before. On software-only, about 10 minutes once you\'ve done it once, since you reuse your previous event. You can also try the sample event in your dashboard before you commit.',
		},
		{
			q: 'Do you provide tablets, or do I bring my own?',
			a: 'Your kitchen team uses tablets, one per cooking station plus one or two for runners. The Standard and Premium bundles include pre-loaded tablets we bring to your venue. If you own tablets already, the software runs in any modern browser.',
		},
		{
			q: 'Can I see a live event before signing up?',
			a: 'Two ways. The fastest is the sample event: sign up, click "Try a sample event", and you can place orders as a guest and watch the kitchen view update on the same screen. For an in-person walk-through, reach out via our demo page and we\'ll run a full event simulation at your office. We don\'t bring prospects to real weddings. That\'s the bride\'s day, not a sales pitch.',
		},
	];

	return (
		<Section id="pricing-faq" bg="white" py={{ base: 24, md: 32 }}>
			<Box maxW="900px" mx="auto" textAlign="center" mb={{ base: 12, md: 16 }}>
				<Kicker>Pricing & FAQ</Kicker>
				<Heading
					as="h2"
					mt={5}
					fontFamily={HEAD_FONT}
					fontWeight="700"
					fontSize={{ base: '36px', md: '56px' }}
					lineHeight="1.04"
					letterSpacing="-0.025em"
					color={INK}
				>
					Questions, answered.
				</Heading>
			</Box>

			<VStack maxW="900px" mx="auto" align="stretch" spacing={3}>
				{items.map((item, i) => {
					const isOpen = open === i;
					return (
						<Box
							key={item.q}
							border={`1px solid ${LINE}`}
							borderRadius="xl"
							bg={isOpen ? CREAM : 'white'}
							transition="background 0.2s ease"
							_hover={{ bg: CREAM }}
						>
							<Box
								as="button"
								onClick={() => setOpen(isOpen ? -1 : i)}
								w="100%"
								textAlign="left"
								px={{ base: 6, md: 8 }}
								py={{ base: 5, md: 6 }}
								bg="transparent"
								border="none"
								cursor="pointer"
							>
								<Flex justify="space-between" align="center" gap={6}>
									<Heading fontFamily={HEAD_FONT} fontSize={{ base: 'lg', md: 'xl' }} fontWeight="600" color={INK}>
										{item.q}
									</Heading>
									<Flex
										w="36px"
										h="36px"
										minW="36px"
										borderRadius="full"
										bg={isOpen ? ACCENT : 'transparent'}
										border={isOpen ? 'none' : `1px solid ${LINE}`}
										color={isOpen ? 'white' : INK}
										align="center"
										justify="center"
										transition="all 0.2s ease"
										transform={isOpen ? 'rotate(45deg)' : 'rotate(0)'}
									>
										<Box as="span" fontSize="20px" fontWeight="300" lineHeight="1">+</Box>
									</Flex>
								</Flex>
							</Box>
							{isOpen && (
								<Box px={{ base: 6, md: 8 }} pb={{ base: 6, md: 7 }}>
									<Text fontSize={{ base: 'md', md: 'lg' }} color={MUTED} lineHeight="1.6">
										{item.a}
									</Text>
								</Box>
							)}
						</Box>
					);
				})}
			</VStack>
		</Section>
	);
};

// =============================================================================
// FINAL CTA
// =============================================================================

const FinalCTA = () => (
	<Box
		as="section"
		bg={BRAND_DEEP}
		color="white"
		py={{ base: 24, md: 36 }}
		px={{ base: 6, md: 10, lg: 16 }}
		position="relative"
		overflow="hidden"
	>
		<Box
			position="absolute"
			bottom="-30%"
			left="-10%"
			w="60%"
			h="100%"
			bg={`radial-gradient(ellipse at center, ${ACCENT}55 0%, transparent 65%)`}
			filter="blur(80px)"
			zIndex={0}
		/>
		<Box
			position="absolute"
			top="-25%"
			right="-15%"
			w="50%"
			h="80%"
			bg={`radial-gradient(ellipse at center, ${BRAND_LIGHT}88 0%, transparent 65%)`}
			filter="blur(80px)"
			zIndex={0}
		/>
		<Container maxW="1400px" px={0} position="relative" zIndex={1}>
			<Box maxW="1100px">
				<Heading
					as="h2"
					fontFamily={HEAD_FONT}
					fontWeight="700"
					fontSize={{ base: '40px', md: '72px', lg: '96px' }}
					lineHeight="0.95"
					letterSpacing="-0.035em"
					color="white"
				>
					Run your next wedding{' '}
					<Box as="span" color={ACCENT}>like a five-star restaurant.</Box>
				</Heading>
				<Text mt={{ base: 6, md: 10 }} fontSize={{ base: 'lg', md: '2xl' }} color="rgba(255,255,255,0.78)" maxW="720px" lineHeight="1.45">
					Your first event is free, and we{'’'}ll be there to run it with you. After that you won{'’'}t need us in the room.
				</Text>
				<Flex mt={{ base: 8, md: 12 }} direction={{ base: 'column', sm: 'row' }} gap={4} maxW={{ base: '100%', sm: 'auto' }}>
					<GetStartedCta label="Try it now" px={10} h={{ base: '56px', md: '64px' }} fontSize={{ base: 'md', md: 'lg' }} w={{ base: '100%', sm: 'auto' }} />
					<Button
						as={RouterLink}
						to="/demo"
						variant="outline"
						borderColor="rgba(255,255,255,0.4)"
						color="white"
						borderRadius="full"
						px={10}
						h={{ base: '56px', md: '64px' }}
						fontSize={{ base: 'md', md: 'lg' }}
						fontWeight="700"
						bg="transparent"
						w={{ base: '100%', sm: 'auto' }}
						textDecoration="none"
						_hover={{ bg: 'rgba(255,255,255,0.06)', borderColor: 'white', textDecoration: 'none' }}
					>
						See a demo
					</Button>
				</Flex>
			</Box>
		</Container>
	</Box>
);

// =============================================================================
// Page wrapper
// =============================================================================

const PlaceholderLanding = () => {
	useEffect(() => {
		// Capture UTM / gclid / fbclid on the LANDING page, not just on
		// /register. Otherwise any visitor who bounces before signing up
		// drops their attribution on the floor.
		captureAttributionFromLocation(window.location.search);
		initializeAnalytics();
		trackPageView('landing');
	}, []);

	return (
		<Box
			className="eventory-landing"
			w="100%"
			minH="100vh"
			bg="white"
			fontFamily={BODY_FONT}
			overflowX="hidden"
			css={{
				scrollBehavior: 'smooth',
				// Bootstrap is loaded globally and adds margin-bottom: 1rem to <p>,
				// which Chakra's <Text> renders as. Reset for this page so flex
				// alignment in HStacks / nav pills doesn't drift.
				'& p': { marginBottom: 0, marginTop: 0 },
			}}
		>
			<TopNav />
			<Hero />
			<TrustStrip />
			<LiveDemo />
			<ProductShowcase />
			<ProofWall />
			<PilotStory />
			<MadeForLagos />
			<ForEveryone />
			<Testimonials />
			<FAQ />
			<FinalCTA />
			<MarketingFooter />
			<StickyWhatsApp />
		</Box>
	);
};

export default PlaceholderLanding;
